import React, { useState, useRef, useEffect } from 'react';
import { Camera, Star, Upload, User, Mail, Edit3, Save, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import UserProfileService from '../../services/userProfileService';
import { normalizeRole, getRoleLabel, isCreator, debugRole } from '../../utils/roleUtils';

const PresentationSection = () => {
  const { user, userProfile, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUpgradingRole, setIsUpgradingRole] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localUserRole, setLocalUserRole] = useState(null); // État local pour le rôle
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    username: '',
    biographie: '',
    phone_number: '',
    avatar_url: ''
  });

  // Charger le profil depuis l'API au montage
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.uid) return;
      
      setIsLoading(true);
      try {
        const response = await UserProfileService.getUserProfile(user.uid);
        if (response.success && response.data) {
          const profile = response.data;
          setFormData({
            username: profile.username || '',
            biographie: profile.biographie || '',
            phone_number: profile.phone_number || '',
            avatar_url: profile.avatar_url || ''
          });
          setLocalUserRole(profile.role); // Mettre à jour le rôle local
        }
      } catch (error) {
        console.error('Erreur chargement profil:', error);
        // Utiliser les données du contexte comme fallback
        setFormData({
          username: userProfile?.username || '',
          biographie: userProfile?.biographie || '',
          phone_number: userProfile?.phone_number || '',
          avatar_url: userProfile?.avatar_url || ''
        });
        setLocalUserRole(userProfile?.role);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user?.uid, userProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifications du fichier
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "L'image doit faire moins de 5MB",
        variant: "destructive"
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Format invalide",
        description: "Veuillez sélectionner une image",
        variant: "destructive"
      });
      return;
    }

    setIsUploadingAvatar(true);
    try {
      // Simulation d'upload (à remplacer par Supabase Storage ou Cloudflare R2)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // URL simulée (remplacer par la vraie URL d'upload)
      const simulatedUrl = `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`;
      
      setFormData(prev => ({
        ...prev,
        avatar_url: simulatedUrl
      }));

      toast({
        title: "Avatar mis à jour !",
        description: "Votre photo de profil a été modifiée avec succès"
      });

    } catch (error) {
      console.error('Erreur upload avatar:', error);
      toast({
        title: "Erreur d'upload",
        description: "Impossible de télécharger l'image",
        variant: "destructive"
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.uid) return;
    
    setIsSaving(true);
    try {
      const response = await UserProfileService.updateUserProfile(user.uid, formData);
      
      if (response.success) {
        toast({
          title: "Profil mis à jour !",
          description: "Vos informations ont été sauvegardées avec succès"
        });

        // Rafraîchir les données du contexte si disponible
        if (refreshUserProfile) {
          await refreshUserProfile();
        }

        setIsEditing(false);
      } else {
        throw new Error(response.message || 'Erreur de sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur de sauvegarde';
      
      toast({
        title: "Erreur de sauvegarde",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBecomeCreator = async () => {
    if (!user?.uid) return;
    
    setIsUpgradingRole(true);
    try {
      console.log('🚀 DÉBUT: Changement vers créateur pour UID:', user.uid);
      
      const response = await UserProfileService.updateUserRole(user.uid, 'creator');
      console.log('📡 RÉPONSE API updateUserRole:', response);
      
      if (response.success) {
        toast({
          title: "Félicitations ! 🎉",
          description: "Vous êtes maintenant créateur ! Vous pouvez ajouter des vidéos."
        });

        // Mettre à jour immédiatement l'état local AVANT le refresh
        setLocalUserRole('creator');
        console.log('✅ État local mis à jour vers creator');

        // Rafraîchir les données du contexte
        if (refreshUserProfile) {
          console.log('🔄 Rafraîchissement du profil AuthContext...');
          await refreshUserProfile();
          console.log('✅ Profil rafraîchi');
        }

        // Vérification finale
        setTimeout(async () => {
          try {
            const verifyResponse = await UserProfileService.getUserProfile(user.uid);
            console.log('🔍 VÉRIFICATION FINALE - Profil après refresh:', verifyResponse);
          } catch (e) {
            console.error('Erreur vérification:', e);
          }
        }, 1000);

      } else {
        throw new Error(response.message || 'Erreur changement de rôle');
      }
    } catch (error) {
      console.error('❌ Erreur upgrade rôle:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur changement de rôle';
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsUpgradingRole(false);
    }
  };

  // Utiliser le rôle local ou celui du contexte, avec normalisation et debug
  const rawRole = localUserRole || userProfile?.role;
  const currentUserRole = normalizeRole(rawRole);
  const userIsCreator = isCreator(rawRole);
  const roleLabel = getRoleLabel(rawRole);

  // Debug: log des rôles pour identifier l'incohérence
  console.log('✅ DEBUG RÔLES CORRIGÉS:', {
    'localUserRole': localUserRole,
    'userProfile_role': userProfile?.role,
    'rawRole_final': rawRole,
    'normalized': currentUserRole,
    'isCreator': userIsCreator,
    'label': roleLabel,
    'email': user?.email
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/home')}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retour à l'accueil</span>
          </Button>
          <h1 className="text-3xl font-bold text-white">Présentation</h1>
        </div>
        {!isEditing ? (
          <Button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-2 bg-pink-600 hover:bg-pink-700"
          >
            <Edit3 className="h-4 w-4" />
            <span>Modifier</span>
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              onClick={handleSaveProfile}
              disabled={isSaving}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{isSaving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </Button>
            <Button
              onClick={() => {
                setIsEditing(false);
                setFormData({
                  username: userProfile?.username || '',
                  biographie: userProfile?.biographie || '',
                  phone_number: userProfile?.phone_number || '',
                  avatar_url: userProfile?.avatar_url || ''
                });
              }}
              variant="outline"
              className="flex items-center space-x-2"
              disabled={isSaving}
            >
              <X className="h-4 w-4" />
              <span>Annuler</span>
            </Button>
          </div>
        )}
      </div>

      {/* Carte de profil */}
      <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-8">
        <div className="flex flex-col lg:flex-row items-start space-y-6 lg:space-y-0 lg:space-x-8">
          
          {/* Avatar */}
          <div className="flex flex-col items-center">
            <div className="relative group">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-pink-500/30 bg-gray-800">
                {formData.avatar_url ? (
                  <img
                    src={formData.avatar_url}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <User className="h-12 w-12" />
                  </div>
                )}
              </div>
              
              {isEditing && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                  className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {isUploadingAvatar ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400" />
                  ) : (
                    <Camera className="h-8 w-8 text-white" />
                  )}
                </button>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />

            {isEditing && (
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                size="sm"
                className="mt-3 flex items-center space-x-2"
                disabled={isUploadingAvatar}
              >
                <Upload className="h-4 w-4" />
                <span>Changer</span>
              </Button>
            )}
          </div>

          {/* Informations */}
          <div className="flex-1 space-y-6">
            {/* Nom d'utilisateur */}
            <div>
              <Label className="text-white mb-2 block">Nom d'utilisateur</Label>
              {isEditing ? (
                <Input
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Votre nom d'utilisateur"
                  className="bg-gray-800/50 border-gray-600 text-white"
                />
              ) : (
                <p className="text-xl font-semibold text-white">{userProfile?.username}</p>
              )}
            </div>

            {/* Email (non modifiable) */}
            <div>
              <Label className="text-white mb-2 block">Email</Label>
              <div className="flex items-center space-x-2">
                <Mail className="h-5 w-5 text-gray-400" />
                <p className="text-gray-300">{user?.email}</p>
              </div>
            </div>

            {/* Biographie */}
            <div>
              <Label className="text-white mb-2 block">Biographie</Label>
              {isEditing ? (
                <Textarea
                  name="biographie"
                  value={formData.biographie}
                  onChange={handleInputChange}
                  placeholder="Parlez-nous de vous..."
                  className="bg-gray-800/50 border-gray-600 text-white min-h-[100px]"
                />
              ) : (
                <p className="text-gray-300 leading-relaxed">
                  {formData.biographie || 'Aucune biographie renseignée.'}
                </p>
              )}
            </div>

            {/* Numéro de téléphone */}
            <div>
              <Label className="text-white mb-2 block">Numéro de téléphone</Label>
              {isEditing ? (
                <Input
                  name="phone_number"
                  type="tel"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  placeholder="+33 6 12 34 56 78"
                  className="bg-gray-800/50 border-gray-600 text-white"
                />
              ) : (
                <p className="text-gray-300">
                  {formData.phone_number || 'Aucun numéro renseigné.'}
                </p>
              )}
            </div>

            {/* Rôle et statut créateur */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gradient-to-r from-violet-600/10 to-pink-600/10 border border-violet-500/20 rounded-xl">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Star className={`h-5 w-5 ${userIsCreator ? 'text-yellow-400' : 'text-gray-400'}`} />
                  <span className="text-white font-semibold">
                    Statut : {roleLabel}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">
                  {userIsCreator 
                    ? 'Vous pouvez uploader et partager vos créations IA'
                    : 'Devenez créateur pour partager vos œuvres avec la communauté'
                  }
                </p>
              </div>

              {!userIsCreator && (
                <Button
                  onClick={handleBecomeCreator}
                  disabled={isUpgradingRole}
                  className="mt-4 sm:mt-0 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold"
                >
                  {isUpgradingRole ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black" />
                      <span>Activation...</span>
                    </div>
                  ) : (
                    'Devenir créateur'
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresentationSection;